import { Link } from "react-router-dom";
import { Heart, BedDouble, Users, Maximize2, MapPin } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { eur, photoUrl, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";

export const FavButton = ({ listingId, initial = false, className = "" }) => {
  const { user, setUser } = useAuth();
  const [fav, setFav] = useState(initial || (user && user.favorites?.includes(listingId)));
  if (!user || user.role !== "tenant") return null;
  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { data } = await api.post(`/favorites/${listingId}`);
    setFav(data.favorite);
    setUser({ ...user, favorites: data.favorite ? [...(user.favorites || []), listingId] : (user.favorites || []).filter((x) => x !== listingId) });
  };
  return (
    <button onClick={toggle} data-testid={`fav-btn-${listingId}`} aria-label="favorite"
      className={`grid h-10 w-10 place-items-center rounded-full bg-white/95 shadow-sm hover:scale-105 transition-transform ${className}`}>
      <Heart className={`h-5 w-5 ${fav ? "fill-[#FF6B00] text-[#FF6B00]" : "text-[#142B5C]"}`} />
    </button>
  );
};

export default function ListingCard({ l, query = "" }) {
  const { t, pick } = useLang();
  return (
    <Link to={`/listing/${l.listing_id}${query}`} className="group block rounded-2xl bg-white border border-slate-200 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300" data-testid={`listing-card-${l.listing_id}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {l.photos?.[0] && <img src={photoUrl(l.photos[0])} alt={l.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />}
        <span className="absolute left-3 top-3 rounded-full bg-white/95 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#142B5C]">{t(`t_${l.property_type}`)}</span>
        <FavButton listingId={l.listing_id} className="absolute right-3 top-3" />
      </div>
      <div className="p-4 sm:p-5 space-y-2.5">
        <p className="flex items-center gap-1 text-xs font-semibold text-slate-500"><MapPin className="h-3.5 w-3.5 text-[#FF6B00]" />{l.city}{l.zone ? ` · ${l.zone}` : ""}</p>
        <h3 className="text-base sm:text-lg font-semibold leading-snug text-[#0B192C] line-clamp-2">{pick(l, "title")}</h3>
        <div className="flex flex-wrap gap-3 text-xs text-slate-600">
          <span className="flex items-center gap-1"><BedDouble className="h-4 w-4" />{l.bedrooms} {t("bedrooms")}</span>
          <span className="flex items-center gap-1"><Users className="h-4 w-4" />{l.max_guests} {t("guests_n")}</span>
          {l.size_sqm > 0 && <span className="flex items-center gap-1"><Maximize2 className="h-4 w-4" />{l.size_sqm} {t("sqm")}</span>}
        </div>
        <div className="flex items-end justify-between pt-2 border-t border-slate-100">
          <p data-testid={`listing-price-${l.listing_id}`}><span className="text-2xl font-extrabold text-[#0B192C] font-display">{eur(l.price_month_public)}</span> <span className="text-sm text-slate-500">{t("per_month")}</span></p>
          <span className="text-xs font-semibold text-[#FF6B00]">{l.min_months > 1 ? t("min_stay", { n: l.min_months }) : t("min_stay_1")}</span>
        </div>
      </div>
    </Link>
  );
}
